// Stage 2, How to Improve. Presentational only.
//
// Shows no mark. The prompt forbids displaying a new one, and the diagnostic's
// mark is already on screen above this section.

import React from 'react';
import {
  Caveat, EvidenceQuotes, FindingHeading, Heading, NullableBlock, Prose,
} from './TaraFeedbackBlocks';

export default function TaraEnrichmentFeedback({ feedback, spans }) {
  if (!feedback) return null;
  const working = feedback.what_is_already_working;
  const stalled = feedback.where_the_reasoning_stalled;
  const move = feedback.the_move_that_would_unlock_the_argument;
  const upgrade = feedback.how_to_upgrade_the_reasoning;
  const practice = feedback.practice_check;

  return (
    <div className="tara-fb-body">
      <NullableBlock value={working}>
        <section className="tara-fb-group">
          {working && working.headline
            ? <FindingHeading>{working.headline}</FindingHeading>
            : <Heading>What is already working</Heading>}
          <Prose>{working && working.explanation}</Prose>
        </section>
      </NullableBlock>

      <NullableBlock value={stalled}>
        <section className="tara-fb-group">
          {stalled && stalled.headline
            ? <FindingHeading>{stalled.headline}</FindingHeading>
            : <Heading>Where the reasoning stalled</Heading>}
          <Prose>{stalled && stalled.explanation}</Prose>
        </section>
      </NullableBlock>

      <NullableBlock value={move}>
        <section className="tara-fb-group">
          <Heading>The move that would unlock the argument</Heading>
          <Prose>{move && move.reasoning_move}</Prose>
          <Prose>{move && move.explanation}</Prose>
          <Prose className="tara-fb-soft">{move && move.why_it_matters_here}</Prose>
        </section>
      </NullableBlock>

      <NullableBlock value={upgrade}>
        <section className="tara-fb-group">
          <Heading>How to upgrade the reasoning</Heading>
          <Prose>{upgrade && upgrade.current_move}</Prose>
          <Prose>{upgrade && upgrade.bridging_logic}</Prose>
          <Prose>{upgrade && upgrade.next_move}</Prose>
          {Array.isArray(upgrade && upgrade.example_upgrade_sentences)
            && upgrade.example_upgrade_sentences.length ? (
              <div className="tara-fb-examples">
                {/* Labelled explicitly. The contract requires these be introduced
                    as an illustration and never passed off as the student's words. */}
                <p className="tara-fb-example-label">
                  {upgrade.illustration_label || 'One possible illustration, not your words'}
                </p>
                {upgrade.example_upgrade_sentences.slice(0, 3).map((s, i) => (
                  <p key={i} className="tara-fb-example">{s}</p>
                ))}
              </div>
            ) : null}
        </section>
      </NullableBlock>

      <Prose>{feedback.how_this_changes_the_final_judgement}</Prose>

      {feedback.reusable_rule_for_future_essays ? (
        <section className="tara-fb-group">
          <Heading>A rule to carry into your next essay</Heading>
          <Prose>{feedback.reusable_rule_for_future_essays}</Prose>
        </section>
      ) : null}

      <NullableBlock value={practice}>
        <section className="tara-fb-group">
          <Heading>Try this</Heading>
          <Prose>{practice && practice.proposition_or_scenario}</Prose>
          <Prose>{practice && practice.instruction}</Prose>
          <Prose className="tara-fb-soft">{practice && practice.expected_time}</Prose>
        </section>
      </NullableBlock>

      {feedback.optional_micro_viva ? (
        <section className="tara-fb-group">
          <Heading>A question to sit with</Heading>
          <Prose>{feedback.optional_micro_viva}</Prose>
        </section>
      ) : null}

      {Array.isArray(feedback.evidence_links) && feedback.evidence_links.length ? (
        <section className="tara-fb-group">
          <Heading>From your essay</Heading>
          {feedback.evidence_links.map((link, i) => (
            <EvidenceQuotes key={i} spans={spans} spanIds={link.evidence_span_ids} />
          ))}
        </section>
      ) : null}

      <Prose className="tara-fb-takeaway">{feedback.enrichment_takeaway}</Prose>
      <Caveat>{feedback.limitations_note}</Caveat>
    </div>
  );
}
